import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Card,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  FormHelperText,
  Divider,
  IconButton,
  CardContent,
  CardActions,
  Tooltip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined as InfoOutlinedIcon,
  LocationOn as LocationIcon,
  LocationOn as LocationOnIcon,
  LocationCity as LocationCityIcon,
  HomeWork as HomeWorkIcon,
  HolidayVillage as HolidayVillageIcon,
  AccountBalance as AccountBalanceIcon,
  Forest as ForestIcon,
  TempleHindu as TempleHinduIcon,
  LocalCafe as LocalCafeIcon,
  Terrain as TerrainIcon,
  Landscape as LandscapeIcon,
  Waves as WavesIcon,
  BeachAccess as BeachAccessIcon,
  Water as WaterIcon,
  AcUnit as AcUnitIcon,
  LocalFireDepartment as LocalFireDepartmentIcon,
  People as PeopleIcon,
  MenuBook as MenuBookIcon,
  AutoAwesome as AutoAwesomeIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Bookmark as BookmarkIcon
} from '@mui/icons-material';
import ProModal from '../../common/ProModal';

// Import the DungeonIcon from the parent component
const DungeonIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M2 7L7 2h10l5 5v10l-5 5H7l-5-5z" />
    <path d="M16 2v5h5" />
    <path d="M7 22v-5H2" />
    <path d="M22 12h-5" />
    <path d="M7 12H2" />
    <path d="M12 7v5" />
    <path d="M12 17v-5" />
  </svg>
);

const LocationEditor = ({ 
  locations = [], 
  onAddLocation, 
  onEditLocation, 
  onDeleteLocation,
  importanceLevels
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const accentColor = isDarkMode ? '#90caf9' : '#0d47a1';
  const accentMuted = isDarkMode ? 'rgba(144, 202, 249, 0.22)' : 'rgba(13, 71, 161, 0.08)';
  const accentBorder = isDarkMode ? 'rgba(144, 202, 249, 0.35)' : 'rgba(13, 71, 161, 0.18)';
  const accentGradient = isDarkMode
    ? 'linear-gradient(135deg, #64b5f6, #1565c0)'
    : 'linear-gradient(135deg, #64b5f6, #0d47a1)';
  const panelBg = isDarkMode ? 'rgba(5, 12, 25, 0.95)' : 'rgba(227, 242, 253, 0.85)';
  const panelBorder = isDarkMode ? 'rgba(144, 202, 249, 0.25)' : 'rgba(13, 71, 161, 0.12)';
  const chipBg = isDarkMode ? 'rgba(144, 202, 249, 0.25)' : 'rgba(21, 101, 192, 0.12)';
  const chipHoverBg = isDarkMode ? 'rgba(144, 202, 249, 0.35)' : 'rgba(21, 101, 192, 0.22)';
  const frostedCardBg = isDarkMode ? 'rgba(8, 17, 34, 0.95)' : '#f6fbff';
  const frostedHeaderBg = isDarkMode ? 'rgba(13, 71, 161, 0.15)' : 'rgba(33, 150, 243, 0.08)';
  const textPrimary = isDarkMode ? '#e3f2fd' : '#0d47a1';
  const textSecondary = isDarkMode ? 'rgba(227, 242, 253, 0.8)' : '#1e3a5f';
  const sectionPaperStyles = {
    p: 2,
    mb: 1,
    borderRadius: 1,
    border: `1px solid ${panelBorder}`,
    bgcolor: panelBg,
    boxShadow: isDarkMode ? '0 10px 30px rgba(0,0,0,0.35)' : '0 12px 30px rgba(13, 71, 161, 0.12)',
    backdropFilter: 'blur(6px)'
  };
  const gradientButtonStyles = {
    borderRadius: 1,
    textTransform: 'none',
    background: accentGradient,
    boxShadow: '0 12px 30px rgba(13, 71, 161, 0.25)',
    '&:hover': {
      boxShadow: '0 18px 40px rgba(13, 71, 161, 0.35)'
    }
  };

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [newLocation, setNewLocation] = useState({
    id: '',
    name: '',
    importance: 'medium',
    description: '',
    locationType: '',
    customLocationType: '',
    climate: '',
    geography: '',
    notableFeatures: '',
    inhabitants: '',
    details: '',
    tags: []
  });
  
  // State for custom location types
  const [customLocationTypes, setCustomLocationTypes] = useState([]);
  const [showCustomTypeDialog, setShowCustomTypeDialog] = useState(false);
  const [newCustomType, setNewCustomType] = useState('');

  // New state for custom templates
  const [customTemplates, setCustomTemplates] = useState([]);
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDescription, setNewTemplateDescription] = useState('');
  const [newTemplateIcon, setNewTemplateIcon] = useState('city');
  
  // Function to handle deleting custom templates
  const handleDeleteCustomTemplate = (templateName) => {
    // Implementation for deleting custom templates
    console.log(`Deleting custom template: ${templateName}`);
    setCustomTemplates(customTemplates.filter(template => template.name !== templateName));
  };
  
  // Function to get tag props for Autocomplete component
  const getTagProps = (params) => {
    return {
      ...params,
      onDelete: () => {
        // Handle tag deletion
        const updatedTags = [...newLocation.tags];
        updatedTags.splice(params.index, 1);
        setNewLocation({ ...newLocation, tags: updatedTags });
      }
    };
  };

  // Handle opening the dialog
  const handleOpenDialog = (location = null) => {
    if (location) {
      setSelectedLocation(location);
      setNewLocation({
        ...location,
        tags: location.tags || []
      });
    } else {
      setSelectedLocation(null);
      setNewLocation({
        id: '',
        name: '',
        importance: 'medium',
        description: '',
        locationType: '',
        customLocationType: '',
        climate: '',
        geography: '',
        notableFeatures: '',
        inhabitants: '',
        details: '',
        tags: []
      });
    }
    setOpenDialog(true);
  };

  // Handle closing the dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // Handle saving the location
  const handleSave = () => {
    if (!newLocation.name) return;
    
    // Process the location data before saving
    const locationToSave = { ...newLocation };
    
    // Handle custom location type
    if (locationToSave.locationType === 'custom' && locationToSave.customLocationType) {
      // Store the display name in a separate field for rendering
      locationToSave.locationTypeDisplay = locationToSave.customLocationType;
      
      // Save this custom type if it's not already in our list
      if (locationToSave.customLocationType.trim() !== '' && 
          !customLocationTypes.includes(locationToSave.customLocationType)) {
        setCustomLocationTypes([...customLocationTypes, locationToSave.customLocationType]);
      }
    } else {
      // For standard types, use the label from the locationTypes array
      const selectedType = locationTypes.find(type => type.value === locationToSave.locationType);
      locationToSave.locationTypeDisplay = selectedType ? selectedType.label : '';
    }
    
    if (selectedLocation) {
      onEditLocation({ ...selectedLocation, ...locationToSave });
    } else {
      onAddLocation({ ...locationToSave, id: Date.now().toString() });
    }
    
    setOpenDialog(false);
  };
  
  // Handle adding a new custom location type
  const handleAddCustomType = () => {
    if (newCustomType.trim() !== '' && !customLocationTypes.includes(newCustomType)) {
      setCustomLocationTypes([...customLocationTypes, newCustomType]);
      setNewCustomType('');
    }
    setShowCustomTypeDialog(false);
  };
  
  // Handle selecting a saved custom type
  const handleSelectCustomType = (type) => {
    setNewLocation({ ...newLocation, customLocationType: type });
  };

  // Function to save the current location as a template
  const saveAsTemplate = () => {
    if (!newTemplateName.trim()) {
      return;
    }
    
    // Create a new template from the current location data
    const newTemplate = {
      name: `custom-${Date.now()}`,
      label: newTemplateName,
      icon: locationTemplates.find(t => t.name === newTemplateIcon)?.icon || <AutoAwesomeIcon fontSize="small" />,
      description: newTemplateDescription || 'Custom template',
      data: {
        locationType: newLocation.locationType || 'custom',
        importance: newLocation.importance || 'medium',
        description: newLocation.description || '',
        climate: newLocation.climate || '',
        geography: newLocation.geography || '',
        notableFeatures: newLocation.notableFeatures || '',
        inhabitants: newLocation.inhabitants || '',
        details: newLocation.details || ''
      },
      isCustom: true
    };
    
    setCustomTemplates([...customTemplates, newTemplate]);
    setShowSaveTemplateDialog(false);
    setNewTemplateName('');
    setNewTemplateDescription('');
    setNewTemplateIcon('city');
  };
  
  // Function to apply a custom template
  const applyCustomTemplate = (templateName) => {
    const template = customTemplates.find(t => t.name === templateName);
    if (template) {
      setNewLocation({
        ...newLocation,
        description: template.data.description,
        geography: template.data.geography,
        notableFeatures: template.data.notableFeatures,
        locationType: template.data.locationType,
        importance: template.data.importance,
        inhabitants: template.data.inhabitants,
        details: template.data.details,
        climate: template.data.climate
      });
    }
  };
  
  // Function to delete a custom template
  const deleteCustomTemplate = (templateName) => {
    setCustomTemplates(customTemplates.filter(t => t.name !== templateName));
  };

  // Location templates - optimized for film/screenwriting with cinematic details
  const locationTemplates = [
    { 
      name: 'city', 
      label: 'City', 
      icon: <LocationCityIcon fontSize="small" />,
      description: 'Urban center with layered society and visual complexity',
      data: {
        locationType: 'city',
        importance: 'high',
        description: 'A sprawling metropolis where wealth and poverty exist side by side. Towering structures cast long shadows over crowded streets.',
        climate: 'Smog hangs in the air during still days. Rain transforms streets into reflective mirrors at night.',
        geography: 'Divided into distinct districts: wealthy heights with panoramic views, industrial zones with smokestacks, cramped lower quarters with narrow alleys.',
        notableFeatures: 'Central plaza with imposing clock tower. Underground network of tunnels and sewers. Rooftop pathways connecting buildings. Night markets that come alive after dark.',
        inhabitants: 'Street vendors, pickpockets, nobles in carriages, city watch patrols, guild members, beggars, and merchants. Everyone has an angle.',
        details: 'Power struggles between merchant guilds and old nobility. Corruption runs deep. The city never sleeps—different dangers emerge at different hours.',
        mood: 'Bustling by day, dangerous by night. Constant background noise of commerce and conflict.',
        visualStyle: 'High contrast lighting. Vertical compositions emphasizing class divide. Steam and smoke creating atmosphere.',
        soundscape: 'Overlapping voices, cart wheels on cobblestone, distant bells, street musicians, shouting vendors.',
        storyHooks: 'Missing persons in the lower quarters. A guild war brewing. Ancient tunnels hiding forgotten secrets. A festival masking sinister plans.'
      }
    },
    { 
      name: 'town', 
      label: 'Town', 
      icon: <HomeWorkIcon fontSize="small" />,
      description: 'Self-contained community with secrets beneath the surface',
      data: {
        locationType: 'town',
        importance: 'medium',
        description: 'A modest town where everyone knows everyone—and everyone\'s business. Prosperity masks underlying tensions.',
        climate: 'Four distinct seasons mark the rhythm of life. Harsh winters drive people indoors; summers bring festivals.',
        geography: 'Main street lined with shops leads to a central square. Residential areas fan outward. A prominent landmark dominates the skyline.',
        notableFeatures: 'The old well in the square (rumored to grant wishes). A boarded-up building no one discusses. The mayor\'s house on the hill.',
        inhabitants: 'Shopkeepers who gossip, a sheriff stretched thin, the wealthy family everyone resents, newcomers viewed with suspicion.',
        details: 'Beneath the friendly facade, old grudges simmer. The town has a dark chapter in its history that shaped current dynamics.',
        mood: 'Deceptively peaceful. Tension lurks beneath pleasantries. Strangers attract attention.',
        visualStyle: 'Warm, nostalgic tones that slowly reveal darker undertones. Wide establishing shots, intimate interiors.',
        soundscape: 'Church bells, children playing, dogs barking, screen doors slamming, crickets at night.',
        storyHooks: 'A stranger arrives asking questions. Something awakens in the old mine. The annual tradition hides a terrible purpose.'
      }
    },
    { 
      name: 'village', 
      label: 'Village', 
      icon: <HolidayVillageIcon fontSize="small" />,
      description: 'Isolated community bound by tradition and superstition',
      data: {
        locationType: 'village',
        importance: 'medium',
        description: 'A remote settlement where old ways persist. The outside world feels distant and distrusted.',
        climate: 'Weather dictates everything—planting, harvest, travel. Storms can isolate the village for days.',
        geography: 'Clustered homes around a common green. Fields stretch to the treeline. A single road leads in and out.',
        notableFeatures: 'The elder\'s cottage. A sacred tree or standing stones. The communal barn where gatherings happen. Unmarked graves at the edge of the woods.',
        inhabitants: 'Weathered farmers, suspicious elders, curious children, the village healer, one family that keeps to themselves.',
        details: 'Rituals passed down for generations—their original meaning forgotten but still observed. Outsiders are tested before being accepted.',
        mood: 'Timeless and eerie. Hospitality with an edge of warning. Nature feels ever-present and watchful.',
        visualStyle: 'Muted earth tones. Low angles emphasizing isolation. Fog and firelight. Faces half in shadow.',
        soundscape: 'Wind through crops, livestock, crackling fires, folk songs, ominous silence at night.',
        storyHooks: 'The harvest failed and someone must answer. A child went missing near the old stones. The village is preparing for a ritual they won\'t explain.'
      }
    },
    { 
      name: 'capital', 
      label: 'Capital City', 
      icon: <AccountBalanceIcon fontSize="small" />,
      description: 'Seat of power where politics is a blood sport',
      data: {
        locationType: 'capital',
        importance: 'critical',
        description: 'The gleaming heart of the realm where power concentrates and conspiracies breed. Every smile hides a dagger.',
        climate: 'Manicured gardens and fountains create artificial paradise. The poor quarters tell a different story.',
        geography: 'Concentric rings of power: palace at center, noble estates, merchant quarters, then sprawling slums against the outer walls.',
        notableFeatures: 'The throne room where fates are decided. The grand cathedral. The infamous prison. Secret passages connecting key buildings. The execution square.',
        inhabitants: 'Scheming nobles, ambitious advisors, royal guards, spies disguised as servants, foreign diplomats, and desperate petitioners.',
        details: 'Three factions vie for the ruler\'s ear. Assassination is common. Public spectacles distract from backroom deals. Nothing is as it appears.',
        mood: 'Opulent and treacherous. Beauty masking rot. Every conversation has subtext.',
        visualStyle: 'Rich colors and gold. Symmetrical, imposing architecture. Characters framed by doorways and columns. Candlelit intrigue.',
        soundscape: 'Whispered conversations, rustling silk, distant fanfares, echoing footsteps in marble halls.',
        storyHooks: 'The heir is not who they claim. A foreign power has infiltrated the court. The ruler is dying and hasn\'t named a successor.'
      }
    },
    { 
      name: 'temple', 
      label: 'Temple/Shrine', 
      icon: <TempleHinduIcon fontSize="small" />,
      description: 'Sacred ground where faith and power intersect',
      data: {
        locationType: 'temple',
        importance: 'high',
        description: 'A place of worship that commands reverence—and fear. The faithful seek salvation; others seek the power within.',
        climate: 'Incense-heavy air. Candlelight flickers eternally. Temperature feels different inside—cooler, heavier.',
        geography: 'Elevated position overlooking surroundings. Approach designed for pilgrimage. Inner sanctums accessible only to the initiated.',
        notableFeatures: 'The altar where offerings are made. Crypts beneath holding ancient relics. Confession chambers. The high priest\'s private quarters. Forbidden archives.',
        inhabitants: 'Devoted priests, silent acolytes, desperate pilgrims, those seeking sanctuary, and perhaps something that dwells in the deepest chambers.',
        details: 'The temple holds political influence rivaling secular powers. Schisms within the clergy. Sacred texts contain dangerous knowledge. Miracles—or clever tricks?',
        mood: 'Reverent and oppressive. Silence that demands respect. A sense of being watched by more than human eyes.',
        visualStyle: 'Shafts of light through high windows. Deep shadows. Vertical lines drawing eyes upward. Gold and crimson.',
        soundscape: 'Chanting, bells, echoing footsteps, dripping water in the crypts, the creak of ancient doors.',
        storyHooks: 'A relic has been stolen. The high priest receives visions of doom. Someone seeks sanctuary from a powerful enemy. The crypt has been disturbed.'
      }
    },
    { 
      name: 'tavern', 
      label: 'Tavern/Inn', 
      icon: <LocalCafeIcon fontSize="small" />,
      description: 'Crossroads of stories where information flows like ale',
      data: {
        locationType: 'tavern',
        importance: 'medium',
        description: 'A weathered establishment where travelers, locals, and trouble all find their way. The drinks are strong and the walls have ears.',
        climate: 'Warm from the hearth, thick with pipe smoke. The smell of ale, roasting meat, and too many bodies.',
        geography: 'Ground floor common room with bar and hearth. Private booths in corners. Creaky stairs to rooms above. Back door to the alley.',
        notableFeatures: 'The bar where the owner holds court. A notice board with jobs and wanted posters. The corner table where deals are made. Rooms with thin walls.',
        inhabitants: 'The gruff but knowing proprietor, a barmaid who sees everything, regulars with their own stools, travelers with stories, and someone in the corner watching.',
        details: 'Neutral ground where enemies share space. The owner has connections. Rooms can be rented by the hour. What happens here stays here—usually.',
        mood: 'Rowdy camaraderie masking watchful tension. Laughter that stops when strangers enter. Warmth against the cold world outside.',
        visualStyle: 'Warm amber lighting. Faces lit by firelight. Smoke hazing the air. Close-ups of hands exchanging coins, notes, secrets.',
        soundscape: 'Clinking mugs, overlapping conversations, a bard in the corner, dice on wood, the creak of floorboards overhead.',
        storyHooks: 'A stranger offers a job too good to be true. Someone recognizes a face from a wanted poster. A fight breaks out over old debts. A dying man stumbles through the door.'
      }
    },
    { 
      name: 'dungeon', 
      label: 'Dungeon/Ruin', 
      icon: <DungeonIcon fontSize="small" />,
      description: 'Forgotten depths where the past refuses to stay buried',
      data: {
        locationType: 'dungeon',
        importance: 'high',
        description: 'A place that should remain sealed. Darkness here is almost physical—torches struggle against it. Something waits below.',
        climate: 'Cold, damp air that clings to skin. The smell of decay and older things. Sound behaves strangely—echoes where there shouldn\'t be.',
        geography: 'Descending levels, each older than the last. Collapsed passages force detours. Architecture shifts from familiar to alien the deeper you go.',
        notableFeatures: 'Warning inscriptions in dead languages. Traps still functional after centuries. Evidence of previous expeditions that didn\'t return. A central chamber holding what was meant to be contained.',
        inhabitants: 'Creatures adapted to darkness. Remnants of original builders. Scavengers. Perhaps the undead. And whatever was imprisoned here.',
        details: 'Built to contain something, not to keep people out. Each level was a failsafe. The deepest chamber was never meant to be opened.',
        mood: 'Claustrophobic dread. The feeling of trespass. Every shadow could hide death. The weight of ages pressing down.',
        visualStyle: 'Near-total darkness pierced by torchlight. Tight framing. Things glimpsed at the edge of light. Dust motes in torch beams.',
        soundscape: 'Dripping water, distant scraping, your own breathing too loud, stones settling, whispers that might be wind.',
        storyHooks: 'A map surfaces showing a sealed entrance. Tremors have opened a new passage. Someone went in and sent back a single, desperate message. What was contained is waking up.'
      }
    },
    { 
      name: 'marketplace', 
      label: 'Marketplace', 
      icon: <LocationIcon fontSize="small" />,
      description: 'Chaotic hub of commerce where anything can be found—for a price',
      data: {
        locationType: 'marketplace',
        importance: 'medium',
        description: 'A sensory assault of colors, sounds, and smells. Merchants hawk wares from across the known world. Fortunes change hands hourly.',
        climate: 'Dust kicked up by crowds. Awnings provide shade. The press of bodies creates its own heat.',
        geography: 'Maze of stalls and tents. Permanent shops ring the edges. Hidden alleys between vendors. A central fountain or statue as landmark.',
        notableFeatures: 'The exotic goods stall with items of questionable origin. The money changer who asks no questions. The food vendors whose aromas guide navigation. The pickpocket children.',
        inhabitants: 'Shouting vendors, haggling customers, city watch on patrol, thieves working the crowd, spies meeting contacts, tourists and locals alike.',
        details: 'Guilds control who sells what and where. Black market goods available if you know who to ask. Information is the most valuable commodity.',
        mood: 'Overwhelming energy. Opportunity and danger in equal measure. Easy to disappear into the crowd.',
        visualStyle: 'Saturated colors, chaotic movement, quick cuts between faces and goods. Tracking shots through the maze of stalls.',
        soundscape: 'Competing vendor calls, haggling in multiple languages, coins clinking, animals, music from street performers.',
        storyHooks: 'A rare item appears that shouldn\'t exist. A contact fails to show at the usual spot. Someone is following through the crowd. A vendor sells something they shouldn\'t have.'
      }
    },
    { 
      name: 'prison', 
      label: 'Prison/Dungeon', 
      icon: <LocationIcon fontSize="small" />,
      description: 'Where hope goes to die and desperate plans are born',
      data: {
        locationType: 'prison',
        importance: 'high',
        description: 'A fortress designed to break spirits. Stone walls thick enough to swallow screams. Some prisoners have been here so long they\'ve forgotten the sun.',
        climate: 'Perpetually cold and damp. No natural light in the deep cells. The air tastes of despair and iron.',
        geography: 'Outer walls patrolled constantly. Administrative areas near the entrance. General population in the middle. The hole—solitary—in the deepest level.',
        notableFeatures: 'The yard where alliances form and scores settle. The warden\'s office overlooking everything. The infirmary where guards don\'t ask questions. The execution chamber.',
        inhabitants: 'Hardened criminals, political prisoners, the wrongly accused, corrupt guards, a warden with absolute power, and the one prisoner everyone fears.',
        details: 'Escape attempts are legend—most end badly. The guards can be bribed but double-cross freely. Gangs control different sections. Someone always knows your secrets.',
        mood: 'Hopeless routine punctuated by violence. Time measured in meals and beatings. Trust is currency and everyone\'s bankrupt.',
        visualStyle: 'Harsh overhead lighting, deep shadows in cells. Bars creating patterns on faces. Muted colors except for blood.',
        soundscape: 'Metal doors slamming, chains dragging, distant shouting, dripping water, the rhythm of guards\' boots.',
        storyHooks: 'A prisoner has information worth killing for. An escape plan needs one more person. A new arrival is not who they claim. The warden is running a side operation.'
      }
    },
    { 
      name: 'harbor', 
      label: 'Harbor/Port', 
      icon: <WavesIcon fontSize="small" />,
      description: 'Gateway to the world where ships bring opportunity and danger',
      data: {
        locationType: 'harbor',
        importance: 'high',
        description: 'Where the sea meets civilization. Ships from distant lands crowd the docks. Sailors spend coin fast, knowing the sea might claim them tomorrow.',
        climate: 'Salt air and constant wind. Fog rolls in without warning. The smell of fish, tar, and brine.',
        geography: 'Wooden docks extending into the water. Warehouses lining the shore. Taverns and brothels in the adjacent district. The harbormaster\'s tower overlooking all.',
        notableFeatures: 'The lighthouse guiding ships home. The dry docks where vessels are repaired. The auction block where cargo—and sometimes people—are sold. The sunken ship visible at low tide.',
        inhabitants: 'Weathered sailors, dock workers, merchants tracking shipments, customs officials (some honest), smugglers, and those waiting for someone who may never return.',
        details: 'Smuggling is an open secret. Ships fly false flags. The harbormaster knows everything and sells information. Pressgangs operate at night.',
        mood: 'Transient and lawless. Everyone is coming or going. The promise of distant horizons. The threat of the deep.',
        visualStyle: 'Wide shots of ships and water. Ropes and rigging creating frames. Fog diffusing light. Weather-beaten faces.',
        soundscape: 'Creaking wood, flapping sails, seabirds, waves against hulls, shouted orders, shanties from taverns.',
        storyHooks: 'A ship arrives with no living crew. Cargo goes missing from a locked warehouse. A sailor has a map to something valuable. Someone needs to leave tonight, no questions asked.'
      }
    },
    { 
      name: 'castle', 
      label: 'Castle/Fortress', 
      icon: <AccountBalanceIcon fontSize="small" />,
      description: 'Stone monument to power built to withstand siege and time',
      data: {
        locationType: 'castle',
        importance: 'critical',
        description: 'Walls that have witnessed centuries of history. Built for war, now a symbol of authority. Its stones could tell stories of triumph and treachery.',
        climate: 'Drafty corridors despite roaring fires. Cold stone that never truly warms. The great hall is the only comfortable space.',
        geography: 'Positioned for defense—hilltop, cliff edge, or river bend. Outer walls, inner keep, towers at corners. A single approach easily defended.',
        notableFeatures: 'The great hall where feasts and judgments occur. The lord\'s chambers in the highest tower. The armory. The dungeon below. Secret passages known only to a few.',
        inhabitants: 'The ruling lord and family, knights and men-at-arms, servants who see everything, a castellan who runs daily operations, prisoners in the depths.',
        details: 'The castle has survived sieges—scars remain. Political marriages and murders have occurred within. Ghosts are rumored. The treasury is legendary.',
        mood: 'Ancient authority. Cold grandeur. Echoes of violence. The weight of legacy and expectation.',
        visualStyle: 'Imposing wide shots. Torchlit interiors with dancing shadows. Banners and heraldry. Figures dwarfed by architecture.',
        soundscape: 'Wind howling through battlements, armor clanking, doors groaning, echoing footsteps, the clatter of the great hall.',
        storyHooks: 'The lord is dying without clear heir. An old enemy approaches with an army. Something stirs in the sealed tower. A servant witnessed something they shouldn\'t have.'
      }
    },
    { 
      name: 'battlefield', 
      label: 'Battlefield', 
      icon: <LocationIcon fontSize="small" />,
      description: 'Scarred earth where history was written in blood',
      data: {
        locationType: 'battlefield',
        importance: 'high',
        description: 'Ground that remembers violence. Whether the battle was yesterday or centuries ago, the land bears witness. Nothing grows quite right here.',
        climate: 'Weather seems drawn to extremes—mud or dust, scorching sun or bitter cold. Crows circle perpetually.',
        geography: 'Terrain that determined tactics—a ridge, a river crossing, open plains. Monuments or mass graves mark significant points. Abandoned equipment rusts.',
        notableFeatures: 'The hill where the commander fell. The tree line where the ambush was sprung. Bones still surface after heavy rain. A memorial no one tends anymore.',
        inhabitants: 'Scavengers picking through remains, pilgrims honoring the dead, historians seeking truth, and perhaps spirits who don\'t know the war ended.',
        details: 'The official history differs from what actually happened. Valuable items remain buried. Local legends warn against staying after dark.',
        mood: 'Haunted silence. The weight of lives lost. Unfinished business. Glory revealed as horror.',
        visualStyle: 'Desaturated colors. Wide desolate shots. Close-ups of weathered artifacts. Mist clinging to low ground.',
        soundscape: 'Wind through grass, distant crows, unnatural silence, and for some—echoes of battle that shouldn\'t be there.',
        storyHooks: 'A dying soldier\'s last words reveal a secret. Something was buried here that someone wants recovered. The dead are restless. A survivor returns seeking closure—or revenge.'
      }
    },
  ];

  // Geographical templates - optimized for film/screenwriting with cinematic details
  const geographicalTemplates = [
    { 
      name: 'forest', 
      label: 'Forest', 
      icon: <ForestIcon fontSize="small" />,
      description: 'Living maze where nature holds dominion',
      data: {
        geography: 'Ancient trees form a cathedral canopy, filtering light into shifting patterns. Undergrowth ranges from impenetrable thickets to eerily clear paths.',
        climate: 'Perpetual twilight beneath the canopy. Mist clings to morning hours. Rain arrives as a delayed percussion through leaves.',
        notableFeatures: 'A clearing where nothing grows. Trees bearing old carvings. A stream that flows the wrong direction. Ruins being slowly consumed by roots.',
        inhabitants: 'Creatures glimpsed at the edge of vision. Hunters who know paths outsiders never find. Something ancient that considers this its domain.',
        mood: 'Watchful and alive. Beauty masking danger. Easy to enter, harder to leave.',
        visualStyle: 'Dappled light and deep shadow. Vertical lines of trunks. Green palette shifting to threatening darkness. Things moving in peripheral vision.',
        soundscape: 'Birdsong that stops suddenly, creaking branches, rustling that follows movement, distant animal calls, your own footsteps too loud.',
        storyHooks: 'Travelers vanish on a known path. Something is killing the trees from within. An old map shows a structure that shouldn\'t exist. The forest is expanding.'
      }
    },
    { 
      name: 'mountain', 
      label: 'Mountain/Range', 
      icon: <TerrainIcon fontSize="small" />,
      description: 'Vertical world where survival demands respect',
      data: {
        geography: 'Peaks pierce the clouds. Treacherous passes connect isolated valleys. Sheer cliffs drop into mist. Caves honeycomb the interior.',
        climate: 'Weather changes without warning—clear skies to blinding storm in minutes. Temperature drops with every step upward. Snow lingers year-round above the treeline.',
        notableFeatures: 'A peak no one has summited and returned. A monastery clinging to impossible cliffs. Mines that delve too deep. Hot springs in unexpected places.',
        inhabitants: 'Mountain folk who distrust lowlanders. Creatures that hunt the high passes. Hermits seeking isolation. Whatever lives in the deep caves.',
        mood: 'Majestic and merciless. Thin air and thinner margins for error. Perspective that makes human concerns seem small.',
        visualStyle: 'Vast scale shots dwarfing figures. Harsh light and deep shadows. Mist obscuring depths. Vertigo-inducing angles.',
        soundscape: 'Howling wind, rockfall echoes, the crunch of snow, labored breathing, avalanche rumble in the distance.',
        storyHooks: 'A pass has become impassable—something guards it. Miners broke through to something unexpected. A signal fire burns on a peak thought unreachable.'
      }
    },
    { 
      name: 'ocean', 
      label: 'Ocean/Sea', 
      icon: <WavesIcon fontSize="small" />,
      description: 'Endless horizon hiding fathomless depths',
      data: {
        geography: 'Water stretching beyond sight. Depths that swallow light. Currents that can carry ships off course for days. Islands appearing like mirages.',
        climate: 'Calm seas lull sailors into complacency before storms strike. Fog banks materialize from nothing. The horizon line between sea and sky blurs.',
        notableFeatures: 'Waters where ships disappear. A current that flows against all others. Reefs that appear on no chart. The place where something massive surfaces.',
        inhabitants: 'Sailors who\'ve seen things they won\'t discuss. Pirates operating from hidden coves. Fishing communities with strange customs. What lives below.',
        mood: 'Freedom and terror in equal measure. The insignificance of humanity. Respect demanded or death delivered.',
        visualStyle: 'Endless blue broken by weather. Ships as isolated worlds. Underwater shots suggesting scale. Storm sequences of primal fury.',
        soundscape: 'Waves against hull, creaking timber, flapping canvas, whale song in the deep, the silence before a storm.',
        storyHooks: 'A ship returns with cargo but no crew. Something follows vessels through a specific stretch of water. A map leads to coordinates that shouldn\'t exist.'
      }
    },
    { 
      name: 'desert', 
      label: 'Desert', 
      icon: <LandscapeIcon fontSize="small" />,
      description: 'Burning expanse where the land itself is hostile',
      data: {
        geography: 'Endless dunes shift like frozen waves. Rocky plateaus offer no shade. Salt flats blind with reflected light. Canyons cut deep into ancient stone.',
        climate: 'Scorching days give way to freezing nights. Sandstorms erase landmarks and bury the unwary. Rain comes once a generation and transforms everything.',
        notableFeatures: 'An oasis that may be mirage. Ruins half-buried in sand. Rock formations that resemble figures. The skeleton of something massive.',
        inhabitants: 'Nomads who read the sands like books. Creatures that hunt by heat. Hermits who sought the emptiness. Whatever built the buried ruins.',
        mood: 'Pitiless and purifying. Hallucination blurs with reality. Time measured in water remaining.',
        visualStyle: 'Bleached colors and harsh shadows. Heat shimmer distorting distance. Vast emptiness with figures as specks. Golden hour beauty before deadly night.',
        soundscape: 'Wind-driven sand, the creak of leather, distant thunder that never brings rain, the silence that rings in ears.',
        storyHooks: 'A sandstorm uncovers something that was meant to stay buried. The oasis has gone dry. Nomads speak of a new danger in the deep desert.'
      }
    },
    { 
      name: 'river', 
      label: 'River/Lake', 
      icon: <WaterIcon fontSize="small" />,
      description: 'Lifeblood of the land with currents that carry fate',
      data: {
        geography: 'Water carving its path through the land. Rapids where the river fights rock. Still pools hiding depth. Banks that flood each season.',
        climate: 'Mist rises at dawn. The water moderates temperature but draws storms. Seasonal floods reshape the landscape.',
        notableFeatures: 'A waterfall hiding caves behind it. The confluence where two rivers meet. A bridge that\'s seen centuries of crossings. The bend where bodies wash up.',
        inhabitants: 'River folk who live on the water. Creatures in the depths. Traders using the current. Those who control the crossings.',
        mood: 'Constant motion toward inevitable destination. Life-giving and life-taking. Boundaries that define territories.',
        visualStyle: 'Reflections doubling the world. Mist softening edges. Current as visual metaphor. Underwater mystery.',
        soundscape: 'Rushing water, creaking boats, fish breaking surface, the hollow sound under bridges, rapids roaring.',
        storyHooks: 'Something is poisoning the water upstream. A body surfaces with a message. The river has changed course overnight. What lives in the deepest pool is hungry.'
      }
    },
    { 
      name: 'arctic', 
      label: 'Arctic/Tundra', 
      icon: <AcUnitIcon fontSize="small" />,
      description: 'Frozen realm where cold is a living enemy',
      data: {
        geography: 'Ice sheets stretching to the horizon. Tundra where permafrost allows nothing to take root. Glaciers calving into frigid seas.',
        climate: 'Cold that kills in minutes of exposure. Months of darkness followed by months of light. Blizzards that last for days.',
        notableFeatures: 'Ice caves glowing blue. The aurora painting the sky. Frozen remains of ancient creatures. A structure that predates any known civilization.',
        inhabitants: 'People who\'ve adapted over generations. Predators in white camouflage. Creatures that shouldn\'t survive but do. What the ice preserved.',
        mood: 'Beautiful and absolutely lethal. Silence so complete it hurts. The edge of the world.',
        visualStyle: 'Blinding white and deep blue. Figures as dark specks against vastness. Aurora colors against darkness. Breath visible in every frame.',
        soundscape: 'Ice cracking, wind that never stops, the crunch of snow, absolute silence between gusts, distant wolf howls.',
        storyHooks: 'The ice is melting and revealing what was frozen within. An expedition went north and sent back one survivor. Something is moving under the ice.'
      }
    },
    { 
      name: 'volcano', 
      label: 'Volcanic Region', 
      icon: <LocalFireDepartmentIcon fontSize="small" />,
      description: 'Land where the earth itself is alive and angry',
      data: {
        geography: 'Cones rising from scarred landscape. Lava fields where nothing grows. Geysers erupting on schedule. Calderas holding lakes of impossible colors.',
        climate: 'Heat radiating from the ground. Sulfur tainting the air. Ash clouds blocking the sun. Unpredictable eruptions reshaping everything.',
        notableFeatures: 'The main crater—dormant or active. Hot springs with healing or deadly properties. Obsidian fields. Temples built to appease what lives below.',
        inhabitants: 'Communities who\'ve learned to read the mountain\'s moods. Creatures that thrive in heat. Priests who claim to speak for the volcano. What dwells in the magma.',
        mood: 'Primal power barely contained. Living on borrowed time. The earth as god.',
        visualStyle: 'Orange and red against black. Smoke and steam obscuring. Silhouettes against lava glow. The mountain dominating every frame.',
        soundscape: 'Rumbling from below, hissing steam, bubbling mud, the roar of eruption, rocks cracking from heat.',
        storyHooks: 'The volcano is waking after centuries of sleep. The sacrifices have stopped and something is displeased. A new vent has opened revealing a passage downward.'
      }
    },
    { 
      name: 'swamp', 
      label: 'Swamp/Wetland', 
      icon: <WaterIcon fontSize="small" />,
      description: 'Treacherous borderland between water and earth',
      data: {
        geography: 'Land and water intertwined without clear boundary. Channels that shift with seasons. Islands of solid ground amid quicksand. Trees rising from murky water.',
        climate: 'Humid air thick enough to taste. Fog that never fully lifts. Insects in clouds. The smell of decay and growth intermingled.',
        notableFeatures: 'The only safe path marked by those who know. Ruins sinking slowly into the muck. Lights that lead travelers astray. The heart of the swamp no one has mapped.',
        inhabitants: 'Swamp dwellers who want to be left alone. Creatures adapted to ambush. Outcasts hiding from the law. Something ancient that remembers when this was dry land.',
        mood: 'Oppressive and disorienting. Every step uncertain. The land itself seems hostile.',
        visualStyle: 'Murky greens and browns. Fog diffusing all light. Reflections hiding what\'s below. Claustrophobic framing despite open space.',
        soundscape: 'Buzzing insects, bubbling mud, splashing of unseen movement, frog chorus, the suck of boots in muck.',
        storyHooks: 'Someone is hiding in the swamp with dangerous knowledge. The safe path has been deliberately destroyed. Lights are appearing that match no natural explanation.'
      }
    },
    { 
      name: 'plains', 
      label: 'Plains/Grassland', 
      icon: <LandscapeIcon fontSize="small" />,
      description: 'Endless horizon where nothing hides',
      data: {
        geography: 'Grass stretching to every horizon. Gentle rolls the only variation. Occasional trees marking water. The sky dominates everything.',
        climate: 'Wind that never stops. Storms visible hours before arrival. Extreme seasons—baking summers, brutal winters. Fire season when the grass becomes tinder.',
        notableFeatures: 'A lone tree that serves as landmark for miles. Stone circles from forgotten peoples. The migration path of vast herds. A depression that might be an ancient impact.',
        inhabitants: 'Nomadic peoples following the herds. Predators that hunt in packs. Settlers trying to tame the land. Whatever digs the massive burrows.',
        mood: 'Exposed and vulnerable. Freedom and loneliness. The weight of sky pressing down.',
        visualStyle: 'Horizontal compositions. Grass rippling like water. Massive sky with dramatic clouds. Figures visible for miles.',
        soundscape: 'Constant wind through grass, distant thunder, herd movement like earthquake, hawk cries, the creak of wagon wheels.',
        storyHooks: 'The herds haven\'t come this season. Something is killing settlers on the frontier. A structure has been spotted that wasn\'t there before.'
      }
    },
  ];

  // Location types (for manual selection)
  const locationTypes = [
    { value: 'city', label: 'City', icon: <LocationCityIcon fontSize="small" /> },
    { value: 'town', label: 'Town', icon: <HomeWorkIcon fontSize="small" /> },
    { value: 'village', label: 'Village', icon: <HolidayVillageIcon fontSize="small" /> },
    { value: 'capital', label: 'Capital', icon: <AccountBalanceIcon fontSize="small" /> },
    { value: 'forest', label: 'Forest', icon: <ForestIcon fontSize="small" /> },
    { value: 'mountain', label: 'Mountain', icon: <TerrainIcon fontSize="small" /> },
    { value: 'temple', label: 'Temple', icon: <TempleHinduIcon fontSize="small" /> },
    { value: 'tavern', label: 'Tavern', icon: <LocalCafeIcon fontSize="small" /> },
    { value: 'dungeon', label: 'Dungeon', icon: <DungeonIcon fontSize="small" /> },
    { value: 'landmark', label: 'Landmark', icon: <LandscapeIcon fontSize="small" /> },
    { value: 'ocean', label: 'Ocean', icon: <WavesIcon fontSize="small" /> },
    { value: 'beach', label: 'Beach', icon: <BeachAccessIcon fontSize="small" /> },
    { value: 'river', label: 'River', icon: <WaterIcon fontSize="small" /> },
    { value: 'arctic', label: 'Arctic', icon: <AcUnitIcon fontSize="small" /> },
    { value: 'volcano', label: 'Volcano', icon: <LocalFireDepartmentIcon fontSize="small" /> },
    { value: 'custom', label: 'Custom Type', icon: <AutoAwesomeIcon fontSize="small" /> },
  ];

  // Function to apply location template
  const applyLocationTemplate = (templateName, currentItem, setItem) => {
    const template = locationTemplates.find(t => t.name === templateName);
    if (template) {
      setItem({
        ...currentItem,
        description: template.data.description,
        geography: template.data.geography,
        climate: template.data.climate,
        notableFeatures: template.data.notableFeatures,
        locationType: template.data.locationType,
        importance: template.data.importance,
        inhabitants: template.data.inhabitants,
        details: template.data.details + (template.data.mood ? `\n\n**Mood:** ${template.data.mood}` : '') +
                 (template.data.visualStyle ? `\n\n**Visual Style:** ${template.data.visualStyle}` : '') +
                 (template.data.soundscape ? `\n\n**Soundscape:** ${template.data.soundscape}` : '') +
                 (template.data.storyHooks ? `\n\n**Story Hooks:** ${template.data.storyHooks}` : '')
      });
      return template;
    }
    return null;
  };

  // Function to apply geographical template
  const applyGeographicalTemplate = (templateName, currentItem, setItem) => {
    const template = geographicalTemplates.find(t => t.name === templateName);
    if (template) {
      const cinematicDetails = 
        (template.data.mood ? `**Mood:** ${template.data.mood}` : '') +
        (template.data.visualStyle ? `\n\n**Visual Style:** ${template.data.visualStyle}` : '') +
        (template.data.soundscape ? `\n\n**Soundscape:** ${template.data.soundscape}` : '') +
        (template.data.storyHooks ? `\n\n**Story Hooks:** ${template.data.storyHooks}` : '');
      
      setItem({
        ...currentItem,
        description: currentItem.description || template.description,
        locationType: template.name,
        geography: template.data.geography,
        climate: template.data.climate,
        notableFeatures: template.data.notableFeatures,
        inhabitants: template.data.inhabitants || currentItem.inhabitants,
        details: cinematicDetails.trim()
      });
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Tooltip title="Add new location" arrow>
          <span>
            <IconButton
              onClick={() => handleOpenDialog()}
              aria-label="Add new location"
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                color: accentColor,
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: accentMuted
                }
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Typography
          variant="h5"
          component="h2"
          sx={{
            fontWeight: 600,
            color: accentColor,
            letterSpacing: 0.5
          }}
        >
          Locations
        </Typography>
      </Box>

      {locations.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 1,
            border: `1px dashed ${accentBorder}`,
            bgcolor: isDarkMode ? 'rgba(13, 30, 50, 0.6)' : 'rgba(227, 242, 253, 0.4)',
            color: textPrimary,
            textAlign: 'center'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            No locations added yet
          </Typography>
          <Typography variant="body2" color={textSecondary}>
            Tap the “+” button to create your first one and start mapping your world.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {locations.map(location => (
            <Grid item xs={12} sm={6} md={4} key={location.id}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                borderRadius: 1,
                overflow: 'hidden',
                boxShadow: isDarkMode ? '0 12px 30px rgba(0,0,0,0.5)' : '0 4px 12px rgba(0,0,0,0.08)',
                transition: 'transform 0.3s, box-shadow 0.3s',
                border: `1px solid ${accentBorder}`,
                background: frostedCardBg,
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: isDarkMode ? '0 18px 35px rgba(0,0,0,0.65)' : '0 12px 20px rgba(0,0,0,0.12)'
                }
              }}>
                <Box sx={{ 
                  p: 1.25, 
                  background: isDarkMode 
                    ? 'linear-gradient(135deg, rgba(33,150,243,0.28), rgba(13,71,161,0.22))'
                    : 'linear-gradient(135deg, rgba(100,181,246,0.35), rgba(13,71,161,0.08))',
                  borderBottom: `1px solid ${accentBorder}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 600,
                    color: textPrimary
                  }}>
                    {location.name}
                  </Typography>
                  {location.importance && (
                    <Chip 
                      size="small" 
                      label={location.importance.charAt(0).toUpperCase() + location.importance.slice(1)} 
                      sx={{ 
                        fontWeight: 500,
                        height: 22,
                        color: accentColor,
                        background: accentMuted,
                        borderRadius: 1,
                        px: 1
                      }}
                    />
                  )}
                </Box>
                <CardContent sx={{ flexGrow: 1, p: 2 }}>
                  {location.description && (
                    <Typography variant="body2" sx={{ mb: 1, color: textSecondary }}>
                      {location.description}
                    </Typography>
                  )}
                  
                  {location.locationType && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5, fontWeight: 600 }}>
                        Type:
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {location.locationTypeDisplay || location.locationType}
                      </Typography>
                    </Box>
                  )}
                  
                  {location.geography && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5, fontWeight: 600 }}>
                        Geography:
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}>
                        {location.geography}
                      </Typography>
                    </Box>
                  )}
                  
                  {location.tags && location.tags.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {location.tags.map((tag, index) => (
                        <Chip 
                          key={index} 
                          label={tag} 
                          size="small"
                          {...getTagProps({ index })} 
                          sx={{ 
                            height: '20px', 
                            fontSize: '0.7rem',
                            bgcolor: chipBg,
                            color: accentColor,
                            borderColor: accentBorder,
                            '&:hover': { bgcolor: chipHoverBg }
                          }} 
                        />
                      ))}
                    </Box>
                  )}
                </CardContent>
                <CardActions sx={{ 
                  p: 1.5, 
                  pt: 0.75,
                  borderTop: `1px solid ${accentBorder}`,
                  justifyContent: 'flex-end',
                  backgroundColor: isDarkMode ? 'rgba(5, 12, 25, 0.35)' : 'rgba(13,71,161,0.03)'
                }}>
                  <Button 
                    size="small" 
                    startIcon={<EditIcon />} 
                    onClick={() => handleOpenDialog(location)}
                    sx={{
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      mr: 1,
                      color: accentColor
                    }}
                  >
                    Edit
                  </Button>
                  <Button 
                    size="small" 
                    startIcon={<DeleteIcon />} 
                    onClick={() => onDeleteLocation(location.id)}
                    sx={{ 
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      color: 'error.main'
                    }}
                  >
                    Delete
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <ProModal
        open={openDialog}
        onClose={handleCloseDialog}
        title={selectedLocation ? 'Edit Location' : 'Add New Location'}
        eyebrow="WORLD BUILDING"
        icon={<LocationIcon />}
        maxWidth="lg"
        actions={(
          <>
            <Button 
              onClick={handleCloseDialog} 
              color="inherit"
              variant="outlined"
              sx={{ 
                borderRadius: '8px',
                textTransform: 'none',
                mr: 1
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              variant="contained"
              disabled={!newLocation.name}
              sx={gradientButtonStyles}
            >
              {selectedLocation ? 'Update Location' : 'Create Location'}
            </Button>
          </>
        )}
        contentProps={{ sx: { pt: 3, px: 3 } }}
      >
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={sectionPaperStyles}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: accentColor, display: 'flex', alignItems: 'center' }}>
                  <InfoOutlinedIcon sx={{ mr: 1, fontSize: '1rem' }} /> Basic Information
                </Typography>
              
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Location Name"
                      fullWidth
                      value={newLocation.name}
                      onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
                      required
                      placeholder="Give your location a memorable name"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                      <InputLabel>Narrative Importance</InputLabel>
                      <Select
                        value={newLocation.importance}
                        label="Narrative Importance"
                        onChange={(e) => setNewLocation({ ...newLocation, importance: e.target.value })}
                      >
                        <MenuItem value="low">Minor Location</MenuItem>
                        <MenuItem value="medium">Supporting Location</MenuItem>
                        <MenuItem value="high">Major Location</MenuItem>
                        <MenuItem value="critical">Central/Pivotal Location</MenuItem>
                      </Select>
                      <FormHelperText>
                        How significant is this location to your story?
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Brief Description"
                      fullWidth
                      multiline
                      rows={2}
                      value={newLocation.description}
                      onChange={(e) => setNewLocation({ ...newLocation, description: e.target.value })}
                      placeholder="A logline for this location—capture its essence in 1-2 sentences as you would describe it to a director or production designer"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            
            {/* Location Classification */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={sectionPaperStyles}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: accentColor, display: 'flex', alignItems: 'center' }}>
                  <LocationCityIcon sx={{ mr: 1, fontSize: '1rem' }} /> Location Classification
                </Typography>
              
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                      <InputLabel>Location Type</InputLabel>
                      <Select
                        value={newLocation.locationType}
                        label="Location Type"
                        onChange={(e) => setNewLocation({ ...newLocation, locationType: e.target.value })}
                      >
                        {locationTypes.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {type.icon}
                              <Box sx={{ ml: 1 }}>{type.label}</Box>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        What kind of settlement or geographical feature is this?
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Custom Location Type"
                      fullWidth
                      value={newLocation.customLocationType}
                      onChange={(e) => setNewLocation({ ...newLocation, customLocationType: e.target.value })}
                      placeholder="Enter a custom location type"
                      sx={{ 
                        '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                        display: newLocation.locationType === 'custom' ? 'block' : 'none'
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AutoAwesomeIcon color="primary" />
                          </InputAdornment>
                        ),
                        endAdornment: newLocation.locationType === 'custom' ? (
                          <InputAdornment position="end">
                            <Button 
                              size="small" 
                              color="primary"
                              onClick={() => setShowCustomTypeDialog(true)}
                              sx={{ minWidth: 'auto' }}
                            >
                              Saved Types
                            </Button>
                          </InputAdornment>
                        ) : null
                      }}
                    />
                    <TextField
                      label="Climate & Atmosphere"
                      fullWidth
                      value={newLocation.climate || ''}
                      onChange={(e) => setNewLocation({ ...newLocation, climate: e.target.value })}
                      placeholder="Weather, lighting conditions, time of day considerations. How does atmosphere affect the mood of scenes here?"
                      sx={{ 
                        '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                        display: newLocation.locationType === 'custom' ? 'none' : 'block'
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            
            {/* Physical Characteristics */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={sectionPaperStyles}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: accentColor, display: 'flex', alignItems: 'center' }}>
                  <TerrainIcon sx={{ mr: 1, fontSize: '1rem' }} /> Physical Characteristics
                </Typography>
              
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Geography & Terrain"
                      fullWidth
                      multiline
                      rows={2}
                      value={newLocation.geography || ''}
                      onChange={(e) => setNewLocation({ ...newLocation, geography: e.target.value })}
                      placeholder="Physical layout for blocking and staging. Consider sight lines, elevation changes, and spatial relationships between areas."
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                      helperText="Think: How would a camera move through this space?"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Notable Features & Landmarks"
                      fullWidth
                      multiline
                      rows={2}
                      value={newLocation.notableFeatures || ''}
                      onChange={(e) => setNewLocation({ ...newLocation, notableFeatures: e.target.value })}
                      placeholder="Visual anchors for establishing shots. Key set pieces that characters interact with. Elements that could become symbolic or thematic."
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                      helperText="What would appear in the establishing shot? What can characters use as props?"
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            
            {/* Cultural & Social Elements */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={sectionPaperStyles}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: accentColor, display: 'flex', alignItems: 'center' }}>
                  <PeopleIcon sx={{ mr: 1, fontSize: '1rem' }} /> Cultural & Social Elements
                </Typography>
              
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Inhabitants & Characters"
                      fullWidth
                      multiline
                      rows={2}
                      value={newLocation.inhabitants || ''}
                      onChange={(e) => setNewLocation({ ...newLocation, inhabitants: e.target.value })}
                      placeholder="Who populates this location? Consider background extras, recurring characters, and character types that belong here."
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                      helperText="Think: Who would your protagonist encounter here? What character archetypes fit this setting?"
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            
            {/* Cinematic & Narrative Details */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={sectionPaperStyles}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: accentColor, display: 'flex', alignItems: 'center' }}>
                  <MenuBookIcon sx={{ mr: 1, fontSize: '1rem' }} /> Cinematic & Story Details
                </Typography>
              
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Story Significance & Plot Connections"
                      fullWidth
                      multiline
                      rows={3}
                      value={newLocation.details || ''}
                      onChange={(e) => setNewLocation({ ...newLocation, details: e.target.value })}
                      placeholder="How does this location serve the story? Include mood, visual style, soundscape, and potential story hooks. What scenes could unfold here?"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                      helperText="Include: Mood/Atmosphere • Visual Style (lighting, colors) • Soundscape • Story Hooks • Scene Potential"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={[]}
                      value={newLocation.tags || []}
                      onChange={(e, newValue) => setNewLocation({ ...newLocation, tags: newValue })}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip 
                            variant="outlined" 
                            label={option} 
                            size="small"
                            {...getTagProps({ index })} 
                            sx={{ 
                              borderColor: accentBorder,
                              color: accentColor,
                              '&:hover': { borderColor: accentColor }
                            }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label="Thematic Tags"
                          placeholder="Add tags"
                          helperText="Press Enter to add a tag (e.g., 'mysterious', 'dangerous', 'sacred')"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            
            {/* Location Templates - Collapsible */}
            <Grid item xs={12}>
              <Accordion 
                defaultExpanded={false}
                sx={{ 
                  bgcolor: isDarkMode ? 'rgba(13, 71, 161, 0.18)' : 'rgba(33, 150, 243, 0.08)', 
                  borderRadius: '1 !important', 
                  border: `1px solid ${panelBorder}`,
                  '&:before': { display: 'none' },
                  boxShadow: 'none'
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon sx={{ color: accentColor }} />}
                  sx={{ 
                    minHeight: 48,
                    '& .MuiAccordionSummary-content': { my: 1 }
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: accentColor, display: 'flex', alignItems: 'center' }}>
                    <AutoAwesomeIcon sx={{ mr: 1, fontSize: '1rem' }} /> Quick Templates
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>
                    Apply a template to quickly populate your location with common characteristics
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: accentColor }}>
                          Settlement Templates
                        </Typography>
                        <Grid container spacing={1}>
                          {locationTemplates.map((template) => (
                            <Grid item xs={6} key={template.name}>
                              <Card 
                                variant="outlined" 
                                sx={{ 
                                  p: 1, 
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  '&:hover': {
                                    bgcolor: 'rgba(33, 150, 243, 0.1)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                                  },
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  borderRadius: '8px',
                                  height: '100%'
                                }}
                                onClick={() => applyLocationTemplate(template.name, newLocation, setNewLocation)}
                              >
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'center',
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '50%',
                                  bgcolor: chipBg,
                                  mb: 1
                                }}>
                                  {template.icon}
                                </Box>
                                <Typography variant="caption" sx={{ textAlign: 'center', fontWeight: 500 }}>
                                  {template.label}
                                </Typography>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: accentColor }}>
                          Natural Location Templates
                        </Typography>
                        <Grid container spacing={1}>
                          {geographicalTemplates.map((template) => (
                            <Grid item xs={6} key={template.name}>
                              <Card 
                                variant="outlined" 
                                sx={{ 
                                  p: 1, 
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  '&:hover': {
                                    bgcolor: 'rgba(33, 150, 243, 0.1)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                                  },
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  borderRadius: '8px',
                                  height: '100%'
                                }}
                                onClick={() => applyGeographicalTemplate(template.name, newLocation, setNewLocation)}
                              >
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'center',
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '50%',
                                  bgcolor: 'rgba(33, 150, 243, 0.1)',
                                  mb: 1
                                }}>
                                  {template.icon}
                                </Box>
                                <Typography variant="caption" sx={{ textAlign: 'center', fontWeight: 500 }}>
                                  {template.label}
                                </Typography>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
            
            {/* Custom Templates */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: isDarkMode ? 'rgba(8, 17, 34, 0.85)' : 'rgba(227, 242, 253, 0.7)', borderRadius: 1, border: `1px solid ${panelBorder}` }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: accentColor, display: 'flex', alignItems: 'center' }}>
                    <BookmarkIcon sx={{ mr: 1, fontSize: '1rem' }} /> Your Custom Templates
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setShowSaveTemplateDialog(true)}
                    sx={{ 
                      ...gradientButtonStyles,
                      px: 2
                    }}
                  >
                    Save Current as Template
                  </Button>
                </Box>
                
                {customTemplates.length > 0 ? (
                  <Grid container spacing={2}>
                    {customTemplates.map((template) => (
                      <Grid item key={template.name} xs={12} sm={6} md={4}>
                        <Card sx={{ 
                          position: 'relative',
                          cursor: 'pointer',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                          }
                        }}
                        onClick={() => applyLocationTemplate(template.name, newLocation, setNewLocation)}
                        >
                          <Box sx={{ 
                            p: 2, 
                            display: 'flex', 
                            alignItems: 'center',
                            borderBottom: '1px solid rgba(0,0,0,0.08)',
                            bgcolor: frostedHeaderBg
                          }}>
                            <Box sx={{ 
                              mr: 1, 
                              display: 'flex', 
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              bgcolor: chipBg
                            }}>
                              {template.icon ? React.cloneElement(template.icon, { fontSize: 'small' }) : <LocationOnIcon fontSize="small" />}
                            </Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {template.label}
                            </Typography>
                            <Box sx={{ ml: 'auto' }}>
                              <IconButton 
                                size="small" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteCustomTemplate(template.name);
                                }}
                                sx={{ color: 'error.main' }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                          <CardContent sx={{ flexGrow: 1, p: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              {template.description || 'Custom location template'}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box sx={{ p: 2, textAlign: 'center', border: `1px dashed ${accentBorder}`, borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      No custom templates yet. Create a location and save it as a template to reuse later.
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
      </ProModal>
      
      <ProModal
        open={showCustomTypeDialog}
        onClose={() => setShowCustomTypeDialog(false)}
        title="Custom Location Types"
        eyebrow="WORLD BUILDING"
        icon={<AutoAwesomeIcon />}
        maxWidth="xs"
        actions={(
          <Button 
            onClick={() => setShowCustomTypeDialog(false)} 
            color="inherit"
            variant="outlined"
            sx={{ 
              borderRadius: '8px',
              textTransform: 'none'
            }}
          >
            Close
          </Button>
        )}
        contentProps={{ sx: { pt: 3, px: 3 } }}
      >
          <Typography variant="body2" sx={{ mb: 2 }}>
            Create and manage custom location types for your world.
          </Typography>
          
          {/* Add new custom type */}
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start' }}>
            <TextField
              label="New Custom Type"
              fullWidth
              value={newCustomType}
              onChange={(e) => setNewCustomType(e.target.value)}
              placeholder="Enter a new location type"
              size="small"
              sx={{ 
                '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                mr: 1
              }}
            />
            <Button 
              variant="contained"
              onClick={handleAddCustomType}
              disabled={!newCustomType.trim()}
              sx={{ 
                ...gradientButtonStyles,
                ml: 1,
                height: '40px'
              }}
            >
              Add
            </Button>
          </Box>
          
          {/* List of saved custom types */}
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Saved Types
          </Typography>
          
          {customLocationTypes.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
              No custom types saved yet. Add one above.
            </Typography>
          ) : (
            <Box sx={{ mb: 2 }}>
              {customLocationTypes.map((type, index) => (
                <Chip
                  key={index}
                  label={type}
                  onClick={() => {
                    handleSelectCustomType(type);
                    setShowCustomTypeDialog(false);
                  }}
                  onDelete={() => {
                    setCustomLocationTypes(customLocationTypes.filter(t => t !== type));
                  }}
                  sx={{ 
                    m: 0.5, 
                    borderRadius: '8px',
                    bgcolor: chipBg,
                    color: accentColor,
                    border: `1px solid ${accentBorder}`,
                    '&:hover': {
                      bgcolor: chipHoverBg,
                    }
                  }}
                />
              ))}
            </Box>
          )}
      </ProModal>
      
      <ProModal
        open={showSaveTemplateDialog}
        onClose={() => setShowSaveTemplateDialog(false)}
        title="Save Template"
        eyebrow="WORLD BUILDING"
        icon={<BookmarkIcon />}
        maxWidth="xs"
        actions={(
          <>
            <Button 
              onClick={() => setShowSaveTemplateDialog(false)} 
              color="inherit"
              variant="outlined"
              sx={{ 
                borderRadius: '8px',
                textTransform: 'none',
                mr: 1
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={saveAsTemplate} 
              variant="contained"
              disabled={!newTemplateName.trim()}
              sx={gradientButtonStyles}
            >
              Save Template
            </Button>
          </>
        )}
        contentProps={{ sx: { pt: 3, px: 3 } }}
      >
          <Typography variant="body2" sx={{ mb: 2 }}>
            Save the current location as a custom template.
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Template Name"
                fullWidth
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                placeholder="Enter a name for the template"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Template Description"
                fullWidth
                value={newTemplateDescription}
                onChange={(e) => setNewTemplateDescription(e.target.value)}
                placeholder="Enter a brief description for the template"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                <InputLabel>Template Icon</InputLabel>
                <Select
                  value={newTemplateIcon}
                  label="Template Icon"
                  onChange={(e) => setNewTemplateIcon(e.target.value)}
                >
                  {locationTemplates.map((template) => (
                    <MenuItem key={template.name} value={template.name}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {React.cloneElement(template.icon, { color: 'primary' })}
                        <Box sx={{ ml: 1 }}>{template.label}</Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
      </ProModal>
    </Box>
  );
};

export default LocationEditor;
